import type { At, SlateEditor } from '@udecode/plate';
import {
  Plate,
  type PlateStoreState,
  type TPlateEditor,
  useEditorPlugin,
} from '@udecode/plate/react';
import { useAIChatEditor } from '@udecode/plate-ai/react';
import { useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import type { TPlateEditorPlugin, TValue } from '../../types';
import { Editor, EditorContainer } from '../ui/editor';
import { AIChatPlugin } from './plugins/ai-plugin/ai-plugins';
import type { ActionType } from './plugins/ai-plugin/types';
import { useCreateEditor } from './use-create-editor';

const AIChatEditor = ({
  content,
  editor,
}: {
  content: { text: string; selection: boolean };
  editor: TPlateEditor<TValue, TPlateEditorPlugin>;
}) => {
  const { api } = useEditorPlugin(AIChatPlugin);

  useAIChatEditor(editor, content.text);

  useEffect(() => {
    if (editor) {
      if (content.selection && editor.children.length > 0) {
        const at = {
          anchor: editor.api.start(editor),
          focus: editor.api.end(editor),
        } as unknown as At;
        editor.tf.select(at, { focus: true }); // С начало и до конца
      } else {
        editor.tf.select([editor.children.length - 1], {
          edge: 'end',
          focus: true,
        }); // Конец
      }

      api.aiChat.show();
    }
  }, [api.aiChat, content.selection, editor]);

  return <Editor variant="onlyAiChat" />;
};

export function PlateEditor({
  content,
  onChange,
  onInsertBelow,
  onInsertAbove,
  onDiscard,
  onReplaceSelection,
}: {
  content: { text: string; selection: boolean };
  onChange?: PlateStoreState<
    TPlateEditor<TValue, TPlateEditorPlugin>
  >['onChange'];
  onInsertBelow?: (editor: SlateEditor) => void;
  onInsertAbove?: (editor: SlateEditor) => void;
  onDiscard?: () => void;
  onReplaceSelection?: (editor: SlateEditor) => void;
}) {
  const editor = useCreateEditor();

  editor.api.aiChat.actions = (type: ActionType, editor?: SlateEditor) => {
    if (type === 'insertBelow' && editor) {
      onInsertBelow?.(editor);
    } else if (type === 'insertAbove' && editor) {
      onInsertAbove?.(editor);
    } else if (type === 'discard') {
      onDiscard?.();
    } else if (type === 'replaceSelection' && editor) {
      onReplaceSelection?.(editor);
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Plate
        editor={editor}
        onChange={options => {
          onChange?.(options);
        }}
      >
        <EditorContainer variant="onlyAiChat">
          <AIChatEditor content={content} editor={editor} />
        </EditorContainer>
      </Plate>
    </DndProvider>
  );
}
